import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, Send, ArrowLeft, Clock, Info,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import ConsultationFooter from "@/components/ConsultationFooter";

const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
const showTzHint = clientTz && clientTz !== "Asia/Kolkata";

export default function SchedulingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);          // { slots, advisor_timezone, ... }
  const [selectedDate, setSelectedDate] = useState();
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("pick");       // pick | otp | confirmed
  const [holdInfo, setHoldInfo] = useState(null);   // { appointment_id, otp, hold_expires_at }
  const [otpInput, setOtpInput] = useState("");
  const [confirmedAt, setConfirmedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: s }] = await Promise.all([
          api.get(`/public/prospects/by-token/${token}`),
          api.get(`/public/available-slots`),
        ]);
        setProspect(p);
        setMeta(s);
      } catch (err) {
        toast.error("This link isn't valid or has expired");
      } finally { setLoading(false); }
    })();
  }, [token]);

  // Group slots by client-local date
  const slotsByDate = useMemo(() => {
    const out = {};
    (meta?.slots || []).forEach((s) => {
      const d = new Date(s.start_utc);
      const key = d.toLocaleDateString("en-CA", { timeZone: clientTz });
      (out[key] ||= []).push(s);
    });
    return out;
  }, [meta]);

  const availableDates = Object.keys(slotsByDate).sort();
  const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: clientTz });
  const selectedKey = selectedDate?.toLocaleDateString("en-CA", { timeZone: clientTz });
  const daySlots = selectedKey ? (slotsByDate[selectedKey] || []) : [];

  const disabled = (date) => {
    const key = date.toLocaleDateString("en-CA", { timeZone: clientTz });
    return !slotsByDate[key];
  };

  const startHold = async () => {
    if (!selectedSlot) { toast.error("Please pick a slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/appointments/hold", {
        prospect_token: token,
        slot_utc: selectedSlot.start_utc,
        client_timezone: clientTz,
      });
      setHoldInfo(data);
      setPhase("otp");
      if (data.otp.mock_otp) {
        toast.info(`Dev mode: OTP is ${data.otp.mock_otp}`, { duration: 8000 });
      } else {
        toast.success(`Verification code sent to ${prospect.email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not hold slot");
    } finally { setBusy(false); }
  };

  const confirmBooking = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/appointments/confirm", {
        appointment_id: holdInfo.appointment_id,
        challenge_id: holdInfo.otp.challenge_id,
        code: otpInput,
      });
      setConfirmedAt(new Date());
      setPhase("confirmed");
      toast.success("You're booked!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!prospect) return (
    <PageShell>
      <div className="p-10 text-center">This link isn't valid.</div>
    </PageShell>
  );

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-12">
        {phase === "confirmed" && (
          <Card className="border-border bg-white p-10 text-center" data-testid="confirmation-card">
            <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-primary" />
            <h1 className="mb-3 text-3xl tracking-tight">You're booked, {prospect.full_name.split(" ")[0]}.</h1>
            <p className="mx-auto max-w-md text-muted-foreground">
              We've reserved <b>{fmtLocal(selectedSlot?.start_utc)}</b> ({clientTz}) for your Discovery Call.
              A confirmation email is on its way to <b>{prospect.email}</b>. The meeting link and preparation
              instructions will follow separately closer to the call.
            </p>
            <div className="mt-8 text-xs text-muted-foreground">
              Nothing is sold on this call.
            </div>
            <a href="/client-login" className="mt-6 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="go-to-portal-link">
              Have an account? Open your portal →
            </a>
            <div className="mt-2">
              <a
                href="https://reliservewealth.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                data-testid="back-to-main-site-confirmed"
              >
                ← Back to reliservewealth.com
              </a>
            </div>
          </Card>
        )}

        {phase === "otp" && (
          <Card className="border-border bg-white p-8" data-testid="otp-card">
            <div className="mb-6 flex items-center gap-3 text-sm text-primary">
              <ShieldCheck className="h-4 w-4" />
              One quick verification and you're done.
            </div>
            <h1 className="mb-2 text-2xl font-dashboard font-bold tracking-tight">Verify your email</h1>
            <p className="mb-6 text-sm text-muted-foreground">
              We sent a 6-digit code to <b>{prospect.email}</b>. Enter it below to lock in your slot
              (<b>{fmtLocal(selectedSlot?.start_utc)}</b>). Check your spam folder if it doesn't arrive within a minute.
            </p>
            {holdInfo?.otp?.mock_otp && (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="mock-otp-banner">
                <b>Dev mode:</b> your OTP is <span className="font-mono font-bold">{holdInfo.otp.mock_otp}</span>
              </div>
            )}
            <Input
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoFocus
              className="text-center text-2xl tracking-[0.5em] font-mono"
              data-testid="otp-input"
            />
            <Button
              onClick={confirmBooking}
              disabled={busy || otpInput.length !== 6}
              className="mt-4 w-full gap-2"
              data-testid="confirm-otp-btn"
            >
              <CheckCircle2 className="h-4 w-4" />
              {busy ? "Confirming…" : "Confirm booking"}
            </Button>
            <button
              onClick={() => { setPhase("pick"); setOtpInput(""); setHoldInfo(null); }}
              className="mt-4 flex w-full items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              data-testid="back-to-slots"
            >
              <ArrowLeft className="h-3 w-3" /> pick a different slot
            </button>
          </Card>
        )}

        {phase === "pick" && (
          <>
            <div className="mb-6 flex items-center gap-3 text-sm text-primary">
              <ShieldCheck className="h-4 w-4" />
              Book a free, no-obligation Discovery Call — nothing is sold on this call.
            </div>
            <h1 className="mb-3 text-3xl tracking-tight sm:text-4xl">Pick a time, {prospect.full_name.split(" ")[0]}.</h1>
            <p className="mb-8 text-muted-foreground">
              About 30 minutes.{showTzHint && (<> All times shown in your local timezone (<b>{clientTz}</b>).</>)}
            </p>

            <Card className="border-border bg-white p-6 md:p-8">
              <div className="grid gap-8 md:grid-cols-[auto_1fr]">
                <div>
                  <div className="overline mb-3 text-muted-foreground">Choose a date</div>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0)) || disabled(date)}
                    className="rounded-md border"
                    data-testid="calendar-picker"
                  />
                  {availableDates.length === 0 && (
                    <p className="mt-3 max-w-[260px] text-xs text-muted-foreground">
                      No slots available in the next {meta?.booking_horizon_days || 15} days. Please check back later.
                    </p>
                  )}
                </div>
                <div>
                  <div className="overline mb-3 text-muted-foreground">Available slots</div>
                  {!selectedDate ? (
                    <p className="text-sm text-muted-foreground">Pick a date to see available slots.</p>
                  ) : daySlots.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No open slots on this date.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {daySlots.map((s) => {
                        const t = new Date(s.start_utc).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: clientTz });
                        const active = selectedSlot?.start_utc === s.start_utc;
                        return (
                          <button
                            key={s.start_utc}
                            onClick={() => setSelectedSlot(s)}
                            data-testid={`slot-${t.replace(":", "")}`}
                            className={
                              "rounded-md border px-2 py-2 text-xs transition-colors duration-200 " +
                              (active
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-white hover:bg-secondary")
                            }
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-6 flex items-center gap-2 rounded-md bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> Slot length: {meta?.slot_length_minutes || 30} min · minimum {meta?.min_notice_hours || 5}h notice
                  </div>
                  <Button
                    className="mt-6 w-full gap-2"
                    onClick={startHold}
                    disabled={busy || !selectedSlot}
                    data-testid="book-slot-btn"
                  >
                    <Send className="h-4 w-4" />
                    {busy ? "Reserving…" : "Reserve slot & email code"}
                  </Button>
                </div>
              </div>
            </Card>
            <p className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              We'll send a 6-digit code to <b>{prospect.email || "your email"}</b> to confirm the booking. The slot is held for 5 minutes while you verify.
            </p>
          </>
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-4">
          <BrandLogo size="sm" />
        </div>
      </header>
      {children}
      <ConsultationFooter />
    </div>
  );
}

function fmtLocal(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: clientTz,
  });
}
